import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronDown, FolderKanban, Layers, Plus, Search, Settings2, X } from 'lucide-react';
import OsScroll from '../ui/OsScroll';
import { useTeamsStore } from '../../store/teamsStore';
import { setActiveTab } from '../../store/uiModalStore';
import { setActiveProject, useProjectScope, useProjectScopeStore } from '../../scope/projectScopeStore';
import { refreshProjectScope } from '../../scope/projectScopeLoader';
import { enqueueScopeJob } from '../../scope/projectScopeJobs';
import { requestProjectCreate } from '../../scope/projectCreateRequest';
import { buildSwitcherSections, switcherLabel } from '../../scope/projectSwitcherModel';
import ProjectWorkspacesGroup from './ProjectWorkspacesGroup';
import './ProjectSwitcher.css';

/**
 * The toolbar project switcher (project scope card 1.2): one scope for API
 * Requests, Boards, Documents and the connection lists. Same trigger, search
 * field, OsScroll list and footer as the workspace switcher next to it
 * (WorkspaceManager), so the two read as one control family; the ✓ sits at
 * the row's end (UI polish rules 13.10.2026), focus stays neutral.
 *
 * With `workspaceControls` (API Requests is open, 21.10.2026) it also carries
 * "API Client › Workspaces" under No project — the project's API workspaces as
 * a submenu — and the separate workspace dropdown is gone from the title bar.
 */
function Dot({ color, kind }) {
  if (kind === 'all') return <Layers size={13} className="project-switcher-icon" aria-hidden />;
  if (kind === 'none') return <FolderKanban size={13} className="project-switcher-icon" aria-hidden />;
  return <span className="project-switcher-dot" style={{ background: color || 'var(--project-switcher-dot-fallback)' }} aria-hidden />;
}

export default function ProjectSwitcher({ workspaceControls = null }) {
  const { t } = useTranslation();
  const scope = useProjectScope();
  const projects = useProjectScopeStore((s) => s.known.projects);
  const teams = useTeamsStore((s) => s.teams);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const labels = {
    all: t('projectScope.all', 'All projects'),
    none: t('projectScope.none', 'No project'),
    personal: t('projectScope.personal', 'Personal'),
    loading: t('projectScope.loading', 'Project…'),
  };
  const teamName = (id) => teams.find((tm) => String(tm.id) === String(id))?.name || t('projectScope.team', 'Team (unknown)');
  const { sections, flat } = useMemo(
    () => buildSwitcherSections({ projects, query, teamName, labels }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [projects, query, teams, t]
  );

  useEffect(() => {
    if (!open) {
      setQuery('');
      return undefined;
    }
    enqueueScopeJob(() => refreshProjectScope().catch(() => {}));
    const id = requestAnimationFrame(() => inputRef.current?.focus());
    return () => cancelAnimationFrame(id);
  }, [open]);
  useEffect(() => setHighlight(0), [query]);
  useEffect(() => {
    if (!open) return;
    listRef.current?.querySelector('[data-highlighted="true"]')?.scrollIntoView({ block: 'nearest' });
  }, [highlight, open]);

  const pick = (row) => {
    if (!row) return;
    setActiveProject(row.key);
    setOpen(false);
  };
  const goProjects = (create) => {
    setOpen(false);
    if (create) requestProjectCreate();
    setActiveTab('projects.panel');
  };

  const label = switcherLabel(scope, labels);
  const active = scope.activeProject;

  return (
    <div className="relative project-switcher">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="project-switcher-trigger flex items-center py-1 px-2 bg-gray-100 border border-gray-200 rounded text-xs"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t('projectScope.switch', 'Switch project')}
      >
        <Dot kind={active ? 'project' : scope.activeProjectId} color={active?.color} />
        <span className="project-switcher-name font-medium text-gray-900">{label}</span>
        <ChevronDown size={13} className="text-gray-500" aria-hidden />
      </button>

      {open && (
        <div
          className="absolute top-full left-0 mt-2 w-72 bg-white border border-gray-200 rounded-lg shadow-lg z-50 flex flex-col overflow-hidden"
          style={{ maxHeight: 'min(420px, calc(100vh - 100px))' }}
          role="listbox"
          aria-label={t('projectScope.projects', 'Projects')}
        >
          <div className="w-full flex-shrink-0 border-b border-gray-100 px-3 py-2">
            <div className="flex w-full min-w-0 items-center gap-2">
              <Search size={14} className="text-gray-400 flex-shrink-0" aria-hidden />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t('projectScope.search', 'Search projects')}
                className="tab-dropdown-search-input min-w-0 w-full flex-1 bg-transparent text-gray-900 placeholder-gray-400 border-none p-0 focus:outline-none"
                autoComplete="off"
                aria-label={t('projectScope.search', 'Search projects')}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    e.stopPropagation();
                    setOpen(false);
                  } else if (e.key === 'ArrowDown') {
                    e.preventDefault();
                    setHighlight((i) => (flat.length ? (i + 1) % flat.length : 0));
                  } else if (e.key === 'ArrowUp') {
                    e.preventDefault();
                    setHighlight((i) => (flat.length ? (i - 1 + flat.length) % flat.length : 0));
                  } else if (e.key === 'Enter') {
                    e.preventDefault();
                    pick(flat[highlight]);
                  }
                }}
              />
              <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center">
                {query ? (
                  <button
                    type="button"
                    className="flex h-full w-full items-center justify-center rounded bg-transparent p-0 cursor-pointer text-gray-900 hover:opacity-70 focus:outline-none"
                    aria-label={t('sidebar.clearSearch')}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      setQuery('');
                      inputRef.current?.focus();
                    }}
                  >
                    <X size={14} strokeWidth={2} aria-hidden />
                  </button>
                ) : null}
              </div>
            </div>
          </div>

          <OsScroll className="flex-1 min-h-0" options={{ overflow: { x: 'hidden' } }}>
            <div ref={listRef} className="project-switcher-list">
              {!flat.length && <div className="px-3 py-6 text-center text-xs text-gray-500">{t('projectScope.noMatch', 'No matching project')}</div>}
              {sections.map((section) => (
                <React.Fragment key={section.id}>
                  {section.label && (
                    <div className="px-3 border-b border-gray-100">
                      <h4 className="text-xxs font-medium text-gray-600 uppercase tracking-wide">{section.label}</h4>
                    </div>
                  )}
                  {section.rows.map((row) => {
                    const index = flat.indexOf(row);
                    const selected = row.key === scope.activeProjectId;
                    return (
                      <div
                        key={row.key}
                        role="option"
                        aria-selected={selected}
                        data-highlighted={index === highlight ? 'true' : 'false'}
                        className={`project-switcher-row flex items-center gap-2 px-3 py-2 cursor-pointer text-sm ${index === highlight ? 'bg-gray-100' : ''}`}
                        onMouseEnter={() => setHighlight(index)}
                        onClick={() => pick(row)}
                      >
                        <Dot kind={row.kind} color={row.project?.color} />
                        <span className="min-w-0 flex-1 truncate text-gray-900">{row.label}</span>
                        {selected && <Check size={14} className="project-switcher-check flex-shrink-0" aria-hidden />}
                      </div>
                    );
                  })}
                  {section.id === 'scope' && workspaceControls && !query && (
                    <ProjectWorkspacesGroup controls={workspaceControls} scope={scope} projects={projects} onDone={() => setOpen(false)} />
                  )}
                </React.Fragment>
              ))}
            </div>
          </OsScroll>

          <div className="project-switcher-footer p-2 border-t border-gray-100 flex-shrink-0 flex flex-col">
            <button type="button" onClick={() => goProjects(true)} className="workspace-dropdown-create-btn flex items-center space-x-2 w-full p-2 rounded-lg text-sm">
              <Plus size={16} />
              <span>{t('projectScope.new', 'New project…')}</span>
            </button>
            <button type="button" onClick={() => goProjects(false)} className="workspace-dropdown-create-btn flex items-center space-x-2 w-full p-2 rounded-lg text-sm">
              <Settings2 size={16} />
              <span>{t('projectScope.manage', 'Manage projects…')}</span>
            </button>
          </div>
        </div>
      )}

      {open && <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />}
    </div>
  );
}
